import { Injectable, computed, signal } from '@angular/core';
import type { Company } from '../../shared/models';

const ACTIVE_COMPANY_STORAGE_KEY = 'taskeo.tenant.active-company-id';
const COMPANY_LANGUAGE_KEY_PREFIX = 'taskeo.company.language';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly companiesSignal = signal<Company[]>([]);
  private readonly activeCompanySignal = signal<Company | null>(null);

  readonly companies = this.companiesSignal.asReadonly();
  readonly activeCompany = this.activeCompanySignal.asReadonly();
  readonly companyId = computed(() => this.activeCompanySignal()?.id ?? null);
  readonly companyKey = computed(() => this.activeCompanySignal()?.companyKey?.trim() ?? null);

  init(
    companies: Company[] = []
  ): void {
    this.setCompanies(companies);
  }

  setCompanies(companies: Company[]): void {
    const withStoredLanguage = companies.map((company) => ({
      ...company,
      language: company.language ?? this.readStoredCompanyLanguage(company.id),
    }));
    this.companiesSignal.set(withStoredLanguage);

    if (!withStoredLanguage.length) {
      this.activeCompanySignal.set(null);
      this.clearStoredCompanyId();
      return;
    }

    const storedId = this.readStoredCompanyId();
    const selected =
      withStoredLanguage.find((company) => company.id === storedId) ??
      withStoredLanguage[0] ??
      null;

    this.activeCompanySignal.set(selected);
    if (selected) {
      this.storeCompanyId(selected.id);
    }
  }

  setActiveCompany(company: Company | string | null): void {
    if (!company) {
      this.activeCompanySignal.set(null);
      this.clearStoredCompanyId();
      return;
    }

    if (typeof company === 'string') {
      this.setActiveCompanyById(company);
      return;
    }

    this.activeCompanySignal.set(company);
    this.storeCompanyId(company.id);
  }

  setActiveCompanyById(companyId: string): void {
    const company = this.companiesSignal().find((item) => item.id === companyId) ?? null;
    this.activeCompanySignal.set(company);
    if (company) {
      this.storeCompanyId(company.id);
    }
  }

  setCompanyLanguage(companyId: string, language: Company['language']): void {
    this.companiesSignal.update((companies) =>
      companies.map((company) => (company.id === companyId ? { ...company, language } : company))
    );

    const activeCompany = this.activeCompanySignal();
    if (activeCompany?.id === companyId) {
      this.activeCompanySignal.set({ ...activeCompany, language });
    }

    this.storeCompanyLanguage(companyId, language);
  }

  getActiveCompanyId(): string | null {
    return this.activeCompanySignal()?.id ?? null;
  }

  private readStoredCompanyId(): string | null {
    try {
      return localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeCompanyId(companyId: string): void {
    try {
      localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, companyId);
    } catch {}
  }

  private clearStoredCompanyId(): void {
    try {
      localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
    } catch {}
  }

  private getCompanyLanguageStorageKey(companyId: string): string {
    return `${COMPANY_LANGUAGE_KEY_PREFIX}.${companyId}`;
  }

  private readStoredCompanyLanguage(companyId: string): Company['language'] {
    try {
      const raw = localStorage.getItem(this.getCompanyLanguageStorageKey(companyId));
      if (raw === 'pt-BR' || raw === 'en-US' || raw === 'es-ES') return raw;
      return null;
    } catch {
      return null;
    }
  }

  private storeCompanyLanguage(companyId: string, language: Company['language']): void {
    try {
      const key = this.getCompanyLanguageStorageKey(companyId);
      if (!language) {
        localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(key, language);
    } catch {}
  }
}
